import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

export interface ImageSelectEvent {
  base64?: string;
  changed: boolean;
  file?: File;
}

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss']
})
export class ImageUploadComponent implements OnInit, OnChanges {
  @Input('placeholder') placeholder = 'assets/images/image-placeholder.jpg';
  @Input('orientation') orientation = 'horizontal';
  @Input('placeholder-type') placeholderType = 'image';
  @Input('disabled') disabled = false;
  @Input() validationsClass!: string;
  @Input() originalUrl: string | null = null;
  @Output() onSelect = new EventEmitter<ImageSelectEvent>();

  isImageValid = true;
  isTypeValid = true;
  isSizeValid = true;
  private originalImageHash: string | null = null;
  private base64: string | null = null;

  // Unique id to pair the hidden input with labels (for attribute)
  readonly inputId = `image-upload-${Math.random().toString(36).slice(2, 9)}`;

  ngOnInit() {
    this.tryHashOriginal();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['originalUrl']) {
      this.tryHashOriginal();
    }
  }

  // Provide current image for preview (base64 > placeholder)
  get currentImage(): string {
    return this.base64 || this.placeholder;
  }

  async changeListener(event: any) {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    if (!file) {
      this.emitSelect(undefined, false);
      // Clear input value to allow re-selecting the same file
      if (event.target) 
        { 
          try 
          { event.target.value = ''; } 
          catch {} 
        }
      return;
    }

    // Validate but don't block the preview
    this.isTypeValid = /^(image\/jpe?g|image\/png)$/i.test(file.type);
    this.isSizeValid = file.size <= 1024 * 1024;
    this.isImageValid = this.isTypeValid && this.isSizeValid;

    // Always read and preview, even if invalid
    const reader = new FileReader();
    reader.onload = async () => {
      this.base64 = reader.result as string;

      // Compute changed (hash-only) only when valid
      let changed = false;

      if (this.isImageValid) {
        try {
          const stagedHash = await this.computeFileHash(file);
          changed = this.originalImageHash !== stagedHash;
        } catch {
          changed = true;
        }
      }

      this.emitSelect(this.base64, changed, file);
      // Clear input value to allow re-selecting the same file
      if (event.target) 
        { try 
          { event.target.value = ''; } 
          catch {} 
        }
    };
    reader.readAsDataURL(file);
  }

  private emitSelect(base64?: string, changed: boolean = false, file?: File) {
    this.onSelect.emit({ base64, changed, file });
  }

  // Allow parent components to recompute the baseline hash explicitly
  public async refreshOriginalHash(): Promise<void> {
    await this.tryHashOriginal();
  }

 
  private async tryHashOriginal() {
    this.originalImageHash = null;
    if (!this.originalUrl) return;
    
    try {
      const h = await this.fetchAndHash(this.originalUrl);

      if (h) this.originalImageHash = h;
    } catch {}
  }

  private async computeFileHash(file: File): Promise<string> {
    const buf = await this.readFileAsArrayBuffer(file);
    const digest = await (window.crypto.subtle as SubtleCrypto).digest('SHA-256', buf as ArrayBuffer);
    
    return this.arrayBufferToHex(digest);
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => { reader.abort(); reject(new Error('Error reading file.')); };
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.readAsArrayBuffer(file);
    });
  }

  private async fetchAndHash(url: string): Promise<string | null> {
    try {
      const res = await fetch(url, { mode: 'cors' });
      
      if (!res.ok) return null;
      
      const buf = await res.arrayBuffer();
      const digest = await (window.crypto.subtle as SubtleCrypto).digest('SHA-256', buf as ArrayBuffer);
      
      return this.arrayBufferToHex(digest);
    } catch {
      return null;
    }
  }

  private arrayBufferToHex(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let string = '';

    for (let i = 0; i < bytes.length; i++) {
      string += ('00' + bytes[i].toString(16)).slice(-2);
    }
    
    return string;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/atoms/image-upload/image-upload.component.ts